import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SITE_LOGO } from "@/constants/assets";
import api from "@/services/api";
import otpService from "@/services/otpService";
import { 
  ChevronLeft, ChevronRight, Phone, Users, Calendar, Heart, 
  Sparkles, DollarSign, MapPin, Palette, Building, SkipForward,
  MessageCircle, Check, Loader2
} from "lucide-react";

// Default fallback options
const DEFAULT_COUNTRY_CODES = [
  { id: 'IN', name: 'India', code: 'IN', dial_code: '91', flag: '🇮🇳' },
  { id: 'US', name: 'United States', code: 'US', dial_code: '1', flag: '🇺🇸' },
  { id: 'GB', name: 'United Kingdom', code: 'GB', dial_code: '44', flag: '🇬🇧' },
  { id: 'AE', name: 'UAE', code: 'AE', dial_code: '971', flag: '🇦🇪' },
  { id: 'SG', name: 'Singapore', code: 'SG', dial_code: '65', flag: '🇸🇬' },
  { id: 'AU', name: 'Australia', code: 'AU', dial_code: '61', flag: '🇦🇺' },
  { id: 'CA', name: 'Canada', code: 'CA', dial_code: '1', flag: '🇨🇦' },
];

const DEFAULT_NUPTIAL_EVENTS = [
  'Pre-wedding Ceremony', 'Ring Ceremony', 'Sangeet', 'Haldi', 'Mehendi', 
  'Garba', 'Beherana', 'Mata Ki Chowki', 'Carnival', 'Pool Party',
  'Baarat', 'Wedding Ceremony', 'Griha Pravesh', 'First Night Room Decor', 
  'Reception', 'Honeymoon Packages', 'Baby Shower', 'Others'
];

const DEFAULT_MAIN_SERVICES = [
  { id: 'wedding_collaterals', name: 'Wedding Collaterals', tags: ['Invitations', 'Save the Dates', 'Menu Cards', 'Signage'] },
  { id: 'welcoming', name: 'Welcoming', tags: ['Welcome Drinks', 'Garland Welcome', 'Aarti', 'Tilak Ceremony'] },
  { id: 'hotel_checkin', name: 'Hotel & Room Check-in', tags: ['Room Decoration', 'Welcome Hampers', 'Guest Coordination'] },
  { id: 'diy_activities', name: 'DIY Activities', tags: ['Mehendi Station', 'Photo Booth', 'Games Corner'] },
  { id: 'baraat', name: 'Baarat Procession', tags: ['Ghodi', 'Buggy', 'Vintage Car', 'Band Baaja', 'Fireworks', 'DJ', 'Swagat Mala'] },
  { id: 'jaymala', name: 'Jaymala', tags: ['Stage Setup', 'Floral Setup', 'Lighting', 'Entry Concept'] },
  { id: 'pheras', name: 'Wedding Pheras', tags: ['Mandap Setup', 'Fire Arrangement', 'Pandit Coordination'] },
  { id: 'vidaai', name: 'Vidaai', tags: ['Car Decoration', 'Farewell Arrangements'] },
  { id: 'reception', name: 'Reception', tags: ['Stage Design', 'Lighting', 'Entertainment', 'Catering Setup'] },
  { id: 'first_night', name: 'First Night Decor', tags: ['Room Decoration', 'Rose Petals', 'Candles'] },
  { id: 'honeymoon', name: 'Honeymoon Packages', tags: ['Domestic', 'International', 'Beach', 'Mountain'] },
  { id: 'overall', name: 'Overall Wedding Services', tags: ['Full Planning', 'Day Coordination', 'Vendor Management'] }
];

const DEFAULT_CITIES = {
  'Preference': ['None - You Recommend Us'],
  'National': ['Goa', 'Udaipur', 'Jodhpur', 'Jaipur', 'Dehradun', 'Kerala', 'Rishikesh', 'Mussoorie', 'Shimla', 'Agra', 'Varanasi', 'Hyderabad', 'Bangalore', 'Chennai', 'Kolkata', 'Jaisalmer', 'Ranthambore', 'Others'],
  'International': ['Turkey', 'Thailand', 'Dubai', 'Egypt', 'Bali', 'Maldives', 'Singapore', 'Malaysia', 'Mauritius', 'Sri Lanka', 'Greece', 'Italy', 'Portugal', 'Spain', 'France', 'Switzerland', 'Others']
};

const DEFAULT_THEMES = {
  'Classic & Elegant': [
    { name: 'Black Tie / Formal', description: 'Glamorous, tuxedos, long gowns, chandeliers' },
    { name: 'Vintage', description: 'Antique décor, lace, old-world charm' },
    { name: 'Romantic', description: 'Soft pastels, flowers, candlelight' }
  ],
  'Nature-Inspired': [
    { name: 'Garden / Floral', description: 'Outdoor, blooms everywhere' },
    { name: 'Rustic / Barn', description: 'Wood, fairy lights, country charm' },
    { name: 'Beach / Nautical', description: 'Sand, sea, soft blues' }
  ],
  'Modern & Minimalist': [
    { name: 'Chic & Contemporary', description: 'Clean lines, neutral colors' },
    { name: 'Industrial', description: 'Exposed brick, metal accents' }
  ]
};

const DEFAULT_BUDGETS = [
  "Below ₹50 Lakhs", "₹50 Lakhs – ₹75 Lakhs", "₹75 Lakhs – ₹1.5 Crores",
  "₹1.5 CR – ₹3 CR", "₹3 CR – ₹5 CR", "₹5 Crores+"
];

const PROPERTY_OPTIONS = {
  hotelSizes: ['80-100', '140-160', '180-200', '240-260', '280-300', '350-370', 'Other'],
  propertyCounts: ['Single', 'Two', 'Three'],
  roomSplits: ['Groom Side', 'Bride Side', 'Artists & Agencies'],
  propertyTypes: ['3 Star', '4 Star', '5 Star', '5 Star Deluxe'],
  budgetsPerNight: ['₹15k-25k', '₹25k-40k', '₹40k-60k', '₹60k-80k', '₹80k-100k', '₹100k-125k', '₹125k+']
};

export default function WeddingForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  
  // OTP State
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    phone: "",
    country_code: "91",
    couple_names: "",
    wedding_date: "",
    guest_count: "",
    main_services: [],
    service_details: {},
    property_hotel_size: "",
    property_count: "",
    property_room_split: [],
    property_type: "",
    property_budget_per_night: "",
    property_triple_occupancy: "",
    property_triple_count: "",
    property_custom_requirement: "",
    wedding_destination: "",
    wedding_destination_other: "",
    nuptial_events: [],
    nuptial_events_other: "",
    styling_themes: [],
    styling_preferences: "",
    has_rituals: null,
    ritual_details: "",
    budget_range: ""
  });
  
  // Form Options (fetched from backend)
  const [formOptions, setFormOptions] = useState({
    countryCodes: DEFAULT_COUNTRY_CODES,
    nuptialEvents: DEFAULT_NUPTIAL_EVENTS,
    mainServices: DEFAULT_MAIN_SERVICES,
    cities: DEFAULT_CITIES,
    themes: DEFAULT_THEMES,
    propertyOptions: PROPERTY_OPTIONS
  });
  
  // Current service for tags selection
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  
  // Fetch form options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await api.getFormOptions();
        if (!response.error) {
          setFormOptions({
            countryCodes: response.countryCodes?.length > 0 ? response.countryCodes : DEFAULT_COUNTRY_CODES,
            nuptialEvents: response.nuptialEvents?.length > 0 ? response.nuptialEvents : DEFAULT_NUPTIAL_EVENTS,
            mainServices: response.mainServices?.length > 0 ? response.mainServices : DEFAULT_MAIN_SERVICES,
            cities: Object.keys(response.cities || {}).length > 0 ? response.cities : DEFAULT_CITIES,
            themes: Object.keys(response.themes || {}).length > 0 ? response.themes : DEFAULT_THEMES,
            propertyOptions: response.propertyOptions || PROPERTY_OPTIONS
          });
        }
      } catch (error) {
        console.error("Failed to fetch form options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, []);

  // Define questions dynamically
  const getQuestions = () => {
    const questions = [
      // Step 0: OTP Verification (handled separately)
      { id: "otp", title: "Verify Your Phone", subtitle: "We'll send an OTP to your WhatsApp", type: "otp", icon: Phone },
      // Step 1: Couple Names
      { id: "couple_names", title: "Names of the Couple", subtitle: "Full names of both partners", type: "text", icon: Heart },
      // Step 2: Wedding Date
      { id: "wedding_date", title: "Desired Wedding Date", subtitle: "When do you envision your celebration?", type: "date", icon: Calendar },
      // Step 3: Guest Count
      { id: "guest_count", title: "Expected Guest Count", subtitle: "How many loved ones will join?", type: "number", icon: Users },
      // Step 4: Main Services (Page 1)
      { id: "main_services", title: "Wedding Services Required", subtitle: "Select the services you need", type: "services", icon: Sparkles },
      // Step 5: Service Tags (Page 2 - for each selected service)
      { id: "service_details", title: "Service Details", subtitle: "Select specific elements for each service", type: "service_tags", icon: Sparkles, skippable: true },
      // Step 6: Property Section (skippable)
      { id: "property", title: "Property Requirements", subtitle: "Tell us about your accommodation needs", type: "property", icon: Building, skippable: true },
      // Step 7: Destination
      { id: "wedding_destination", title: "Do you dream of a mountaintop wedding or a ceremony by the waves?", subtitle: "Select your dream destination", type: "destination", icon: MapPin },
      // Step 8: Nuptial Events
      { id: "nuptial_events", title: "Nuptial Events", subtitle: "Which celebrations are you planning?", type: "multiselect", options: formOptions.nuptialEvents, icon: Sparkles },
      // Step 9: Themes
      { id: "styling_themes", title: "Your Wedding Vision", subtitle: "Select themes that resonate with your dream wedding", type: "themes", icon: Palette },
      // Step 10: Rituals
      { id: "has_rituals", title: "Special Rituals & Traditions", subtitle: "Do you have specific customs to incorporate?", type: "yesno", icon: Heart },
      // Step 11: Budget
      { id: "budget_range", title: "Estimated Budget", subtitle: "Select your investment range", type: "select", options: DEFAULT_BUDGETS, icon: DollarSign }
    ];
    return questions;
  };

  const QUESTIONS = getQuestions();
  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;

  const goToStep = (stepIndex) => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(stepIndex);
      setAnimating(false);
    }, 300);
  };

  // Send OTP - Direct API call
  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    setOtpSending(true);
    try {
      const response = await otpService.sendOtp(phoneNumber, countryCode);
      console.log('OTP Response:', response);
      
      if (response.success) {
        setTransactionId(response.transaction_id);
        setShowOtpInput(true);
        toast.success("OTP sent to your WhatsApp!");
      } else {
        toast.error(response.error || "Failed to send OTP");
      }
    } catch (error) {
      console.error('OTP Error:', error);
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  // Verify OTP - Direct API call
  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      toast.error("Please enter the OTP");
      return;
    }
    
    setOtpVerifying(true);
    try {
      const response = await otpService.verifyOtp(transactionId, otpCode);
      console.log('Verify Response:', response);
      
      if (response.success && response.verified) {
        setOtpVerified(true);
        setFormData(prev => ({
          ...prev,
          phone: phoneNumber,
          country_code: countryCode
        }));
        toast.success("Phone verified successfully!");
        goToStep(1);
      } else {
        toast.error(response.error || "Invalid OTP");
      }
    } catch (error) {
      console.error('Verify Error:', error);
      toast.error("Failed to verify OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  // Validation
  const isCurrentStepValid = useCallback(() => {
    if (!currentQuestion) return false;
    
    if (currentQuestion.type === "otp") {
      return otpVerified;
    }
    if (currentQuestion.skippable) return true;
    
    const value = formData[currentQuestion.id];
    if (currentQuestion.type === "multiselect" || currentQuestion.type === "themes" || currentQuestion.type === "services") {
      return value && value.length > 0;
    }
    if (currentQuestion.type === "yesno") {
      return value !== null;
    }
    if (currentQuestion.type === "destination") {
      return formData.wedding_destination && formData.wedding_destination.trim() !== "";
    }
    if (currentQuestion.type === "service_tags") {
      return true; // Always valid (skippable)
    }
    if (currentQuestion.type === "property") {
      return true; // Always valid (skippable)
    }
    return value && value.toString().trim() !== "";
  }, [currentQuestion, formData, otpVerified]);

  const handleNext = () => {
    if (currentQuestion.type === "otp") {
      if (!otpVerified) {
        toast.error("Please verify your phone first");
        return;
      }
      goToStep(currentStep + 1);
      return;
    }
    if (!isCurrentStepValid()) {
      toast.error("Please complete this question");
      return;
    }
    // Date future validation — only checked on Continue
    if (currentQuestion.type === "date") {
      const val = formData[currentQuestion.id];
      const today = new Date().toISOString().split('T')[0];
      if (!val || val < today) {
        toast.error("Please select a future date");
        return;
      }
    }
    
    if (currentStep < QUESTIONS.length - 1) {
      goToStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setAnimating(false);
      }, 300);
    }
  };

  const handleSkip = () => {
    if (currentQuestion.skippable) {
      handleNext();
    }
  };

  // Input handlers
  const handleInputChange = (value) => {
    setFormData(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const handleMultiSelect = (option) => {
    const current = formData[currentQuestion.id] || [];
    if (current.includes(option)) {
      handleInputChange(current.filter(item => item !== option));
    } else {
      handleInputChange([...current, option]);
    }
  };

  const handleServiceSelect = (serviceName) => {
    const current = formData.main_services || [];
    if (current.includes(serviceName)) {
      setFormData(prev => ({
        ...prev,
        main_services: current.filter(s => s !== serviceName),
        service_details: { ...prev.service_details, [serviceName]: undefined }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        main_services: [...current, serviceName]
      }));
    }
  };

  const handleServiceTagSelect = (serviceName, tag) => {
    const currentTags = formData.service_details[serviceName] || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    setFormData(prev => ({
      ...prev,
      service_details: { ...prev.service_details, [serviceName]: newTags }
    }));
  };

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        guest_count: parseInt(formData.guest_count),
        has_rituals: formData.has_rituals === true
      };
      
      const response = await api.createSubmission(payload);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      navigate("/thank-you", {
        state: {
          submission: response.submission,
          pdfToken: response.pdf_token
        }
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !["textarea", "otp"].includes(currentQuestion.type)) {
      e.preventDefault();
      handleNext();
    }
  };

  const IconComponent = currentQuestion?.icon || Heart;

  // Check if "Other" or "Others" is selected
  const isOtherSelected = (fieldId) => {
    const value = formData[fieldId] || [];
    return value.some(v => v.toLowerCase().includes('other'));
  };

  if (loadingOptions) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" data-testid="wedding-form">
      {/* Progress Bar */}
      <div className="progress-bar">
        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Header */}
      <header className="py-6 px-6 flex justify-center">
        <img
          src={SITE_LOGO}
          alt="Inventive Pages"
          className="h-[25rem] object-contain"
          data-testid="logo"
        />
      </header>

      {/* Form Container */}
      <main className="flex-1 flex items-center justify-center px-6 py-4">
        <div className="w-full max-w-2xl">
          {/* Question Counter */}
          <div className="text-center mb-6">
            <span className="text-[#c4646a] text-sm tracking-widest">
              STEP {currentStep + 1} OF {QUESTIONS.length}
            </span>
          </div>

          {/* Question Card */}
          <div className={`transition-all duration-350 ${animating ? "opacity-0 translate-y-6 scale-98" : "opacity-100 translate-y-0 scale-100"}`}
               style={{ transition: 'opacity 0.35s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1)' }}>
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-[#c4646a]/10 flex items-center justify-center animate-float"
                   style={{ animationDuration: '3.5s' }}>
                <IconComponent className="w-7 h-7 text-[#c4646a]" />
              </div>
            </div>

            {/* Question Title */}
            <h2 className="text-2xl md:text-3xl font-light text-center mb-2 animate-fade-in-up" data-testid="question-title"
                style={{ animationDelay: '50ms' }}>
              {currentQuestion.title}
            </h2>
            <p className="text-gray-400 text-center mb-8 text-sm animate-fade-in-up"
               style={{ animationDelay: '100ms' }}>
              {currentQuestion.subtitle}
            </p>

            {/* Input Fields */}
            <div className="space-y-4">
              {/* OTP Verification */}
              {currentQuestion.type === "otp" && (
                <div className="space-y-4">
                  {!showOtpInput ? (
                    <>
                      {/* Country Code + Phone */}
                      <div className="flex gap-2">
                        <select
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          className="w-32 px-3 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#c4646a]"
                        >
                          {formOptions.countryCodes.map((c) => (
                            <option key={c.id} value={c.dial_code} className="bg-[#1a1a1a]">
                              {c.flag} +{c.dial_code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          className="flex-1 luxury-input"
                          placeholder="Enter phone number"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                          maxLength={10}
                        />
                      </div>
                      <button
                        onClick={handleSendOtp}
                        disabled={otpSending || phoneNumber.length < 10}
                        className="w-full luxury-button flex items-center justify-center gap-2"
                      >
                        {otpSending ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>
                        ) : (
                          <><MessageCircle className="w-5 h-5" /> Send OTP via WhatsApp</>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-center text-gray-300">
                        OTP sent to <span className="text-[#c4646a]">+{countryCode} {phoneNumber}</span>
                      </p>
                      <input
                        type="text"
                        className="luxury-input text-center text-2xl tracking-widest"
                        placeholder="Enter OTP"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        maxLength={6}
                        autoFocus
                      />
                      <button
                        onClick={handleVerifyOtp}
                        disabled={otpVerifying || otpCode.length < 4}
                        className="w-full luxury-button flex items-center justify-center gap-2"
                      >
                        {otpVerifying ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Verifying...</>
                        ) : (
                          <><Check className="w-5 h-5" /> Verify OTP</>
                        )}
                      </button>
                      <button
                        onClick={() => { setShowOtpInput(false); setOtpCode(''); }}
                        className="w-full text-gray-400 hover:text-white text-sm"
                      >
                        Change phone number
                      </button>
                    </>
                  )}
                  {otpVerified && (
                    <div className="text-center text-green-400 flex items-center justify-center gap-2">
                      <Check className="w-5 h-5" /> Phone verified!
                    </div>
                  )}
                </div>
              )}

              {/* Text Input */}
              {currentQuestion.type === "text" && (
                <input
                  type="text"
                  className="luxury-input text-center"
                  placeholder="Enter here..."
                  value={formData[currentQuestion.id]}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              )}

              {/* Number Input */}
              {currentQuestion.type === "number" && (
                <input
                  type="number"
                  className="luxury-input text-center"
                  placeholder="e.g., 500"
                  value={formData[currentQuestion.id]}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  min="1"
                />
              )}

              {/* Date Input */}
              {currentQuestion.type === "date" && (
                <input
                  type="date"
                  className="luxury-input text-center"
                  value={formData[currentQuestion.id]}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              )}

              {/* Main Services Selection */}
              {currentQuestion.type === "services" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2 stagger-children">
                  {formOptions.mainServices.map((service) => (
                    <div
                      key={service.id || service.name}
                      className={`luxury-option animate-fade-in-up ${formData.main_services?.includes(service.name) ? "selected" : ""}`}
                      onClick={() => handleServiceSelect(service.name)}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        formData.main_services?.includes(service.name)
                          ? "bg-[#c4646a] border-[#c4646a]"
                          : "border-gray-500"
                      }`}>
                        {formData.main_services?.includes(service.name) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span>{service.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Service Tags Selection */}
              {currentQuestion.type === "service_tags" && (
                <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                  {formData.main_services?.length > 0 ? (
                    formData.main_services.map((serviceName) => {
                      const service = formOptions.mainServices.find(s => s.name === serviceName);
                      if (!service || !service.tags?.length) return null;
                      
                      return (
                        <div key={serviceName} className="border border-white/10 rounded-lg p-4">
                          <h4 className="text-[#c4646a] font-medium mb-3">{serviceName}</h4>
                          <div className="flex flex-wrap gap-2">
                            {service.tags.map((tag) => (
                              <button
                                key={tag}
                                onClick={() => handleServiceTagSelect(serviceName, tag)}
                                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                  formData.service_details[serviceName]?.includes(tag)
                                    ? "bg-[#c4646a] text-white"
                                    : "bg-white/5 text-gray-300 hover:bg-white/10"
                                }`}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-400 text-center py-8">No services selected. You can skip this step.</p>
                  )}
                </div>
              )}

              {/* Property Section */}
              {currentQuestion.type === "property" && (
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                  {/* Hotel Size */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Hotel Size (rooms)</label>
                    <div className="flex flex-wrap gap-2">
                      {formOptions.propertyOptions.hotelSizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setFormData(prev => ({ ...prev, property_hotel_size: size }))}
                          className={`px-4 py-2 rounded-lg text-sm transition-all ${
                            formData.property_hotel_size === size
                              ? "bg-[#c4646a] text-white"
                              : "bg-white/5 text-gray-300 hover:bg-white/10"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Property Count */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Number of Properties</label>
                    <div className="flex gap-2">
                      {formOptions.propertyOptions.propertyCounts.map((count) => (
                        <button
                          key={count}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            property_count: count,
                            // reset room split when changing count
                            property_room_split: []
                          }))}
                          className={`px-4 py-2 rounded-lg text-sm flex-1 transition-all ${
                            formData.property_count === count
                              ? "bg-[#c4646a] text-white"
                              : "bg-white/5 text-gray-300 hover:bg-white/10"
                          }`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Room Split - hidden for Single; max 2 for Two; max 3 for Three */}
                  {(formData.property_count === 'Two' || formData.property_count === 'Three') && (
                    <div>
                      <label className="text-gray-400 text-sm mb-2 block">
                        Room Split
                        <span className="ml-2 text-gray-500">
                          (select {formData.property_count === 'Two' ? 'up to 2' : 'up to 3'})
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {formOptions.propertyOptions.roomSplits.map((split) => {
                          const maxAllowed = formData.property_count === 'Two' ? 2 : 3;
                          const current = formData.property_room_split || [];
                          const isSelected = current.includes(split);
                          const isDisabled = !isSelected && current.length >= maxAllowed;
                          return (
                            <button
                              key={split}
                              disabled={isDisabled}
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  property_room_split: isSelected
                                    ? current.filter(s => s !== split)
                                    : [...current, split]
                                }));
                              }}
                              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                                isSelected
                                  ? "bg-[#c4646a] text-white"
                                  : isDisabled
                                  ? "bg-white/5 text-gray-600 cursor-not-allowed"
                                  : "bg-white/5 text-gray-300 hover:bg-white/10"
                              }`}
                            >
                              {split}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Property Type */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Property Type</label>
                    <div className="flex flex-wrap gap-2">
                      {formOptions.propertyOptions.propertyTypes.map((type) => (
                        <button
                          key={type}
                          onClick={() => setFormData(prev => ({ ...prev, property_type: type }))}
                          className={`px-4 py-2 rounded-lg text-sm transition-all ${
                            formData.property_type === type
                              ? "bg-[#c4646a] text-white"
                              : "bg-white/5 text-gray-300 hover:bg-white/10"
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Budget per Night */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Budget per Room per Night</label>
                    <div className="flex flex-wrap gap-2">
                      {formOptions.propertyOptions.budgetsPerNight.map((budget) => (
                        <button
                          key={budget}
                          onClick={() => setFormData(prev => ({ ...prev, property_budget_per_night: budget }))}
                          className={`px-4 py-2 rounded-lg text-sm transition-all ${
                            formData.property_budget_per_night === budget
                              ? "bg-[#c4646a] text-white"
                              : "bg-white/5 text-gray-300 hover:bg-white/10"
                          }`}
                        >
                          {budget}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Triple Occupancy */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Triple Occupancy Required?</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, property_triple_occupancy: 'Yes' }))}
                        className={`px-4 py-2 rounded-lg text-sm flex-1 transition-all ${
                          formData.property_triple_occupancy === 'Yes'
                            ? "bg-[#c4646a] text-white"
                            : "bg-white/5 text-gray-300 hover:bg-white/10"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, property_triple_occupancy: 'No', property_triple_count: '' }))}
                        className={`px-4 py-2 rounded-lg text-sm flex-1 transition-all ${
                          formData.property_triple_occupancy === 'No'
                            ? "bg-[#c4646a] text-white"
                            : "bg-white/5 text-gray-300 hover:bg-white/10"
                        }`}
                      >
                        No
                      </button>
                    </div>
                    {formData.property_triple_occupancy === 'Yes' && (
                      <input
                        type="number"
                        className="luxury-input mt-2"
                        placeholder="Number of triple occupancy rooms"
                        value={formData.property_triple_count}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_triple_count: e.target.value }))}
                      />
                    )}
                  </div>

                  {/* Custom Requirement */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Custom Requirements (Optional)</label>
                    <textarea
                      className="luxury-input min-h-[80px]"
                      placeholder="Any specific property requirements..."
                      value={formData.property_custom_requirement}
                      onChange={(e) => setFormData(prev => ({ ...prev, property_custom_requirement: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {/* Destination Selection */}
              {currentQuestion.type === "destination" && (
                <>
                  <div className="space-y-6 max-h-[420px] overflow-y-auto pr-2">
                    {Object.entries(formOptions.cities)
                      .filter(([category]) => category.toLowerCase() !== 'preference')
                      .map(([category, cities]) => (
                      <div key={category}>
                        <h4 className="text-[#c4646a] text-sm font-medium mb-3 uppercase tracking-wider">{category}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {cities.map((city) => (
                            <div
                              key={city}
                              className={`luxury-option justify-center text-sm py-3 ${formData.wedding_destination === city ? "selected" : ""}`}
                              onClick={() => setFormData(prev => ({ ...prev, wedding_destination: city }))}
                            >
                              <span>{city}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.wedding_destination?.toLowerCase().includes('other') && (
                    <div className="mt-4">
                      <input
                        type="text"
                        className="luxury-input"
                        placeholder="Please specify your preferred destination..."
                        value={formData.wedding_destination_other}
                        onChange={(e) => setFormData(prev => ({ ...prev, wedding_destination_other: e.target.value }))}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Multi-select */}
              {currentQuestion.type === "multiselect" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 stagger-children">
                    {currentQuestion.options.map((option) => (
                      <div
                        key={option}
                        className={`luxury-option animate-fade-in-up ${formData[currentQuestion.id]?.includes(option) ? "selected" : ""}`}
                        onClick={() => handleMultiSelect(option)}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          formData[currentQuestion.id]?.includes(option)
                            ? "bg-[#c4646a] border-[#c4646a]"
                            : "border-gray-500"
                        }`}>
                          {formData[currentQuestion.id]?.includes(option) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span>{option}</span>
                      </div>
                    ))}
                  </div>
                  {isOtherSelected(currentQuestion.id) && (
                    <div className="mt-4">
                      <input
                        type="text"
                        className="luxury-input"
                        placeholder={`Please specify other ${currentQuestion.id === 'nuptial_events' ? 'events' : 'items'}...`}
                        value={formData[`${currentQuestion.id}_other`] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [`${currentQuestion.id}_other`]: e.target.value }))}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Themes Selection */}
              {currentQuestion.type === "themes" && (
                <>
                  <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                    {Object.entries(formOptions.themes).map(([category, themes]) => (
                      <div key={category}>
                        <h4 className="text-[#c4646a] text-sm font-medium mb-3 uppercase tracking-wider sticky top-0 bg-[#0a0a0a] py-2">{category}</h4>
                        <div className="space-y-2">
                          {themes.map((theme) => (
                            <div
                              key={theme.name}
                              className={`luxury-option flex-col items-start text-left py-3 ${formData.styling_themes?.includes(theme.name) ? "selected" : ""}`}
                              onClick={() => handleMultiSelect(theme.name)}
                            >
                              <div className="flex items-center gap-3 w-full">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                  formData.styling_themes?.includes(theme.name)
                                    ? "bg-[#c4646a] border-[#c4646a]"
                                    : "border-gray-500"
                                }`}>
                                  {formData.styling_themes?.includes(theme.name) && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div>
                                  <span className="font-medium">{theme.name}</span>
                                  {theme.description && (
                                    <p className="text-gray-400 text-sm mt-1">{theme.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <label className="block text-sm text-gray-400 mb-2">Additional Vision Details (Optional)</label>
                    <textarea
                      className="luxury-input min-h-[100px]"
                      placeholder="Share more about your vision..."
                      value={formData.styling_preferences}
                      onChange={(e) => setFormData(prev => ({ ...prev, styling_preferences: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {/* Single Select */}
              {currentQuestion.type === "select" && (
                <div className="space-y-3">
                  {currentQuestion.options.map((option) => (
                    <div
                      key={option}
                      className={`luxury-option justify-center ${formData[currentQuestion.id] === option ? "selected" : ""}`}
                      onClick={() => handleInputChange(option)}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        formData[currentQuestion.id] === option
                          ? "border-[#c4646a]"
                          : "border-gray-500"
                      }`}>
                        {formData[currentQuestion.id] === option && (
                          <div className="w-2.5 h-2.5 rounded-full bg-[#c4646a]" />
                        )}
                      </div>
                      <span>{option}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Yes/No */}
              {currentQuestion.type === "yesno" && (
                <>
                  <div className="flex gap-4 justify-center">
                    <button
                      className={`luxury-option flex-1 max-w-[200px] justify-center ${formData[currentQuestion.id] === true ? "selected" : ""}`}
                      onClick={() => handleInputChange(true)}
                    >
                      <span>Yes</span>
                    </button>
                    <button
                      className={`luxury-option flex-1 max-w-[200px] justify-center ${formData[currentQuestion.id] === false ? "selected" : ""}`}
                      onClick={() => handleInputChange(false)}
                    >
                      <span>No</span>
                    </button>
                  </div>
                  {formData[currentQuestion.id] === true && currentQuestion.id === "has_rituals" && (
                    <div className="mt-6">
                      <textarea
                        className="luxury-input min-h-[100px]"
                        placeholder="Please describe the rituals or traditions..."
                        value={formData.ritual_details}
                        onChange={(e) => setFormData(prev => ({ ...prev, ritual_details: e.target.value }))}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-10">
            <button
              className={`flex items-center gap-2 text-gray-400 hover:text-white transition-colors ${currentStep === 0 ? "invisible" : ""}`}
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div className="flex items-center gap-3">
              {currentQuestion.skippable && (
                <button
                  onClick={handleSkip}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                >
                  <SkipForward className="w-4 h-4" />
                  <span>Skip</span>
                </button>
              )}

              {currentQuestion.type === "otp" && otpVerified ? (
                <button
                  className="luxury-button flex items-center gap-2"
                  onClick={() => goToStep(1)}
                >
                  <span>Continue</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : currentQuestion.type !== "otp" ? (
                <button
                  className="luxury-button flex items-center gap-2"
                  onClick={handleNext}
                  disabled={submitting || (!currentQuestion.skippable && !isCurrentStepValid())}
                >
                  {submitting ? (
                    <span>Submitting...</span>
                  ) : currentStep === QUESTIONS.length - 1 ? (
                    <span>Submit</span>
                  ) : (
                    <>
                      <span>Continue</span>
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-gray-600 text-sm">
        <p>Your information is secure and confidential</p>
      </footer>
    </div>
  );
}
